import React, {useContext, useMemo, useRef, useState} from "react";
import {
	Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
	Grid, Stack, TextField, Typography, useTheme
} from "@mui/material";
import AncillaryDiscountSelect from "../../create-ra/ra-filling/ancillaries-info/AncillaryDiscountSelect";
import ChangeGroupMultiSelect, {IChangeGroupOption} from "../../create-ra/ra-filling/ancillaries-info/ChangeGroupMultiSelect";
import {partitionChangeGroup} from "../../create-ra/ra-filling/ancillaries-info/changeGroup";
import {isCoverageDisabledByMutualExclusion, getEffectiveSubtypes} from "../../../shared/coverageMutualExclusion";
import NumberInput from "../../../../util/component/NumberInput";
import {Inventory2Outlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useMutation} from "@tanstack/react-query";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import CustomLoader from "../../../../util/component/CustomLoader";
import {IRADAncillary} from "../../models/models";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {LANGUAGES, LOCALES} from "../../config";
import {IAncillaryDiscountRange} from "../../create-ra/ra-filling/ancillaries-info/discount";
import {addAncillariesPostSignBatch, removeAncillariesPostSignBatch} from "../services/http.services";
import AncillarySignatureBox from "./AncillarySignatureBox";
import PostSignAdditionalDriver from "./PostSignAdditionalDriver";
import {
	additionalDriverUnitsCap, isAdditionalDriverAncillary, isAddItemComplete, isAddressMissing, isEtollBlocked,
	isMileage, needsAddress
} from "./postSignAddRules";
import {
	IAddAncillariesBatchRequest, IAddAncillaryItem, IAncillaryDeskRules, IDigitSignPair,
	IRemoveAncillariesBatchRequest
} from "./models";

export type PostSignMode = 'add' | 'remove';

export interface IAncillaryPostSignActionsProps {
	idRentalAgreement: string;
	/** L'RA deve essere ONGOING per consentire le operazioni post-firma (VT-01). */
	isOngoing: boolean;
	/** Token di concorrenza ottimistica del RA (VT-13). */
	lastRequestUpdateDate: string;
	/** Ancillary vendibili al banco dopo la firma (flag_resellable_at_desk_post_ra). */
	addableAncillaries: IRADAncillary[];
	/** Ancillary rimovibili (origine desk, entro time_to_remove). */
	removableAncillaries: IRADAncillary[];
	/** Ancillary già presenti sull'RA (booked + counterSale) per la mutua esclusione coperture. */
	existingAncillaries?: IRADAncillary[];
	/** Regole di listino PRC per l'ancillary scelto. */
	resolveDeskRules: (ancillary: IRADAncillary) => IAncillaryDeskRules;
	/** Grant SCOPE_RA_ANCILLARY_ADD. */
	canAdd: boolean;
	/** Grant SCOPE_RA_ANCILLARY_REMOVE. */
	canRemove: boolean;
	/** Range sconto banco dalla governance (location x profili). Assente → fallback documentato. */
	deskDiscountRange?: IAncillaryDiscountRange | null;
	/** Callback invocata dopo un'operazione riuscita (refresh storico/dettaglio). */
	onCompleted?: () => void;
	/** LPP-25233: guidatori aggiuntivi gia' sul RA (slot firstDriver/driver occupati). */
	existingAdditionalDrivers?: number;
	/** LPP-25233: apparato telepedaggio del veicolo del RA; vuoto = telepedaggio non vendibile. */
	vehicleEtollNumber?: string | null;
}

/** LPP-25233: sezione dati di un guidatore aggiuntivo (una per unita'), chiave stabile per la rimozione puntuale. */
interface IDriverSlot {
	key: number;
	cbId: string | null;
}

/**
 * Azioni di vendita/rimozione ancillary post-firma su RA ONGOING (LPP-22289 / SCFE-1,
 * RDT §2.1(2),(3),(7),(11)). Consente di gestire in un unico blocco <b>N accessori</b> (aggiunta o
 * rimozione) con <b>una sola firma</b> a mano digit-sign del CLIENTE (RF-03); l'invio e' atomico
 * (endpoint batch NTW). VT-02: {@code idRentalAgreement} resta invariato.
 *
 * <p>Presentazione allineata al flusso accessori di create-ra: ogni accessorio e' una riga sempre
 * visibile con stepper unita' +/− (se maxUnits &gt; 1) o checkbox e selettore sconto banco; niente
 * "aggiungi alla lista". Alla conferma vengono inviati tutti gli accessori con unita' &gt; 0.</p>
 */
const AncillaryPostSignActions = (props: IAncillaryPostSignActionsProps) => {
	const {
		idRentalAgreement, isOngoing, lastRequestUpdateDate, addableAncillaries, removableAncillaries,
		existingAncillaries, resolveDeskRules, canAdd, canRemove, deskDiscountRange, onCompleted,
		existingAdditionalDrivers = 0, vehicleEtollNumber
	} = props;

	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const {globalData, dispatchGlobalData} = useContext(GlobalContext);

	const locale = useMemo(() => getLocaleByLang(i18n.language) as LOCALES, [i18n.language]);

	// Profili LPP dell'operatore, notificati a PRC per la lookup del range di governance (feedback 6).
	const idsProfile = useMemo(
		() => (globalData?.user?.profiles ?? []).map((p: any) => p?.id).filter(Boolean) as number[],
		[globalData?.user?.profiles]
	);

	// Chiave del nome localizzato in base alla lingua corrente (allineata al dettaglio/emissione).
	const ancillaryNameKey = useMemo(() => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});
		return prop;
	}, [i18n.language]);

	// Nome leggibile dell'accessorio con fallback robusto (evita codici/"Undefined" — feedback 2/6).
	const resolveName = (a: any): string =>
		(a?.[ancillaryNameKey] as string)
		?? a?.ancillaryNameIt
		?? a?.ancillaryDefaultLabel
		?? a?.ancillaryDefaultName
		?? a?.ancillaryLabel
		?? a?.ancillaryCode;

	const byCode = useMemo(() => {
		const map = new Map<string, IRADAncillary>();
		[...addableAncillaries, ...removableAncillaries].forEach((a) => map.set(a.ancillaryCode, a));
		return map;
	}, [addableAncillaries, removableAncillaries]);

	const [mode, setMode] = useState<PostSignMode | null>(null);
	// ADD: unita' e sconto per accessorio (units > 0 => selezionato).
	const [addUnits, setAddUnits] = useState<Record<string, number>>({});
	const [addDiscount, setAddDiscount] = useState<Record<string, number | null>>({});
	// ADD: chilometraggio (accessori mileageNeeded) e indirizzo (DELIVERY/COLLECTION) per accessorio.
	const [addMileage, setAddMileage] = useState<Record<string, number>>({});
	const [addAddress, setAddAddress] = useState<Record<string, string>>({});
	// ADD (LPP-25233): un guidatore per unita' di ADDITIONAL_DRIVER, con il cbId quando e' pronto.
	const [driverSlots, setDriverSlots] = useState<IDriverSlot[]>([]);
	const nextDriverSlotKey = useRef(0);
	// REMOVE: codici selezionati (multi).
	const [removeCodes, setRemoveCodes] = useState<string[]>([]);
	// REMOVE: unita' da rimuovere per accessorio (R2/LPP-21950). Chiave assente = tutte le unita'.
	const [removeUnits, setRemoveUnits] = useState<Record<string, number>>({});
	// Firma unica per l'intero blocco.
	const [signatures, setSignatures] = useState<IDigitSignPair>({customerDigitSign: null});
	const [signCustomerPresent, setSignCustomerPresent] = useState<boolean>(false);

	const notify = (message: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: setSnackbar,
			payload: {snackbar: {message: t(message), type, show: true, autoClose: true}}
		});
	};

	// NTW rifiuta add/remove post-firma con AncillaryOperationNotAllowedException.Reason (VT-13,
	// finestre time_to_change/time_to_remove, mutua esclusione coperture, ...); sc-be lo impacchetta
	// in ErrorResponse.errorDetails (stringa JSON {reason,message}). Estrai il reason e mostra il
	// messaggio specifico invece del generico add/removeError. Fallback al generico se reason assente
	// o non mappato.
	const errorKeyFor = (err: unknown, fallback: string): string => {
		let reason: string | undefined;
		try {
			const data = (err as any)?.response?.data;
			const details = data?.errorDetails ?? data;
			const parsed = typeof details === 'string' ? JSON.parse(details) : details;
			// LPP-25233: ERROR_TOLL_ANCILLARY arriva come codice semplice (stringa JSON), non come {reason,message}.
			reason = parsed?.reason ?? data?.reason ?? (typeof parsed === 'string' ? parsed : undefined);
		} catch {
			// errorDetails non JSON -> fallback generico
		}
		const key = reason ? `raDetail.ancillaries.postSign.message.reason.${reason}` : '';
		return key && i18n.exists(`network:${key}`) ? key : fallback;
	};

	const resetForm = () => {
		setAddUnits({});
		setAddDiscount({});
		setAddMileage({});
		setAddAddress({});
		setDriverSlots([]);
		setRemoveCodes([]);
		setRemoveUnits({});
		setSignatures({customerDigitSign: null});
		setSignCustomerPresent(false);
	};

	const handleOpen = (m: PostSignMode) => {
		resetForm();
		setMode(m);
	};

	const handleClose = () => {
		setMode(null);
		resetForm();
	};

	const label = (a: IRADAncillary) => resolveName(a);
	const catalogMaxUnitsOf = (a: IRADAncillary) => (a.maxUnits != null && a.maxUnits > 0 ? a.maxUnits : 1);
	// LPP-25233: il guidatore aggiuntivo non puo' superare gli slot liberi del RA (max 2, come in emissione).
	const maxUnitsOf = (a: IRADAncillary) => isAdditionalDriverAncillary(a)
		? additionalDriverUnitsCap(catalogMaxUnitsOf(a), existingAdditionalDrivers)
		: catalogMaxUnitsOf(a);
	const isMultiUnit = (a: IRADAncillary) => maxUnitsOf(a) > 1;
	const unitsOf = (code: string) => addUnits[code] ?? 0;
	// R2 (LPP-21950) — REMOVE: le unita' rimovibili sono quelle PRESENTI sulla riga (ancillaryUnits),
	// non maxUnits del catalogo: sugli accessori post-firma maxUnits non arriva da PRC e vale 0
	// (stessa ragione del fallback in AncillariesInfo.AncillaryField).
	const removableUnitsOf = (a: IRADAncillary) => ((a.ancillaryUnits ?? 0) > 0 ? a.ancillaryUnits : 1);
	const isMultiUnitRemovable = (a: IRADAncillary) => removableUnitsOf(a) > 1;
	// Default: si rimuovono TUTTE le unita' (comportamento storico); l'operatore puo' ridurle.
	const removeUnitsOf = (a: IRADAncillary) => removeUnits[a.ancillaryCode] ?? removableUnitsOf(a);
	const setRemoveUnitsValue = (a: IRADAncillary, value: number) => {
		const clamped = Math.max(1, Math.min(value, removableUnitsOf(a)));
		setRemoveUnits((prev) => ({...prev, [a.ancillaryCode]: clamped}));
	};
	const mileageOf = (code: string) => addMileage[code] ?? 0;
	const addressOf = (code: string) => addAddress[code] ?? '';

	const clearItemExtras = (code: string) => {
		setAddDiscount((prev) => ({...prev, [code]: null}));
		setAddMileage((prev) => ({...prev, [code]: 0}));
		setAddAddress((prev) => ({...prev, [code]: ''}));
	};

	const setUnits = (a: IRADAncillary, value: number) => {
		const clamped = Math.max(0, Math.min(value, maxUnitsOf(a)));
		setAddUnits((prev) => ({...prev, [a.ancillaryCode]: clamped}));
		if (clamped === 0) {
			clearItemExtras(a.ancillaryCode);
		}
		if (isAdditionalDriverAncillary(a)) {
			// Una sezione guidatore per unita': si aggiungono/tolgono in coda, le altre restano intatte.
			setDriverSlots((prev) => clamped <= prev.length
				? prev.slice(0, clamped)
				: [...prev, ...Array.from({length: clamped - prev.length}, () => ({key: nextDriverSlotKey.current++, cbId: null}))]);
		}
	};

	/** Rimozione di un guidatore dal suo form: toglie proprio quella sezione e un'unita'. */
	const removeDriverSlot = (a: IRADAncillary, key: number) => {
		const units = Math.max(0, unitsOf(a.ancillaryCode) - 1);
		setDriverSlots((prev) => prev.filter((s) => s.key !== key));
		setAddUnits((prev) => ({...prev, [a.ancillaryCode]: units}));
		if (units === 0) {
			clearItemExtras(a.ancillaryCode);
		}
	};

	const setDriverSlotCbId = (key: number, cbId: string | null) =>
		setDriverSlots((prev) => prev.map((s) => (s.key === key ? {...s, cbId} : s)));

	// Accessori a chilometraggio (mileageNeeded): la selezione deriva dal km inserito (>0 => selezionato).
	const setMileage = (a: IRADAncillary, value: number) => {
		const clamped = Math.max(0, Math.min(value, 999));
		setAddMileage((prev) => ({...prev, [a.ancillaryCode]: clamped}));
		setAddUnits((prev) => ({...prev, [a.ancillaryCode]: clamped > 0 ? 1 : 0}));
		if (clamped === 0) {
			setAddDiscount((prev) => ({...prev, [a.ancillaryCode]: null}));
			setAddAddress((prev) => ({...prev, [a.ancillaryCode]: ''}));
		}
	};

	const setAddressValue = (a: IRADAncillary, value: string) =>
		setAddAddress((prev) => ({...prev, [a.ancillaryCode]: value}));

	const toggleAdd = (a: IRADAncillary, checked: boolean) => setUnits(a, checked ? 1 : 0);

	const toggleRemove = (code: string, checked: boolean) =>
		setRemoveCodes((prev) => (checked ? [...prev, code] : prev.filter((c) => c !== code)));

	const addMutation = useMutation<any, unknown, IAddAncillariesBatchRequest>(addAncillariesPostSignBatch, {
		onSuccess: () => {
			notify('raDetail.ancillaries.postSign.message.addSuccess', snackbarTypes.success);
			handleClose();
			onCompleted?.();
		},
		onError: (err) => notify(errorKeyFor(err, 'raDetail.ancillaries.postSign.message.addError'))
	});

	const removeMutation = useMutation<any, unknown, IRemoveAncillariesBatchRequest>(removeAncillariesPostSignBatch, {
		onSuccess: () => {
			notify('raDetail.ancillaries.postSign.message.removeSuccess', snackbarTypes.success);
			handleClose();
			onCompleted?.();
		},
		onError: (err) => notify(errorKeyFor(err, 'raDetail.ancillaries.postSign.message.removeError'))
	});

	const isLoading = addMutation.isLoading || removeMutation.isLoading;

	const selectedAddCodes = useMemo(
		() => addableAncillaries.filter((a) => unitsOf(a.ancillaryCode) > 0).map((a) => a.ancillaryCode),
		[addableAncillaries, addUnits]
	);
	const hasSelection = mode === 'add' ? selectedAddCodes.length > 0 : removeCodes.length > 0;
	// LPP-25233: indirizzo (consegna/ritiro), km e guidatori aggiuntivi completi, come in emissione.
	const addItemsComplete = mode !== 'add' || addableAncillaries.every((a) => isAddItemComplete({
		ancillary: a,
		units: unitsOf(a.ancillaryCode),
		mileage: mileageOf(a.ancillaryCode),
		address: addressOf(a.ancillaryCode),
		driverCbIds: isAdditionalDriverAncillary(a) ? driverSlots.map((s) => s.cbId) : []
	}));
	// RF-03: la firma del CLIENTE e' condizione di conclusione dell'intero blocco (sola firma cliente).
	const canConfirm = hasSelection && addItemsComplete && signCustomerPresent && !isLoading;

	const handleConfirm = () => {
		if (!hasSelection || !addItemsComplete || !signCustomerPresent) {
			return;
		}

		if (mode === 'add') {
			const items: IAddAncillaryItem[] = addableAncillaries
				.filter((a) => unitsOf(a.ancillaryCode) > 0)
				.map((a) => {
					const discount = addDiscount[a.ancillaryCode] ?? null;
					const anyA = a as any;
					return {
						ancillaryCode: a.ancillaryCode,
						ancillarySubType: a.ancillarySubType as string,
						// LPP-23150: il sell type arriva dal catalogo post-firma (NtwCounterSaleAncillary.sellType).
						ancillarySellType: anyA.sellType ?? anyA.ancillarySellType ?? null,
						ancillaryServiceItemCode: a.ancillaryServiceItemCode,
						ancillaryDefaultName: anyA.ancillaryDefaultName ?? a.ancillaryDefaultLabel,
						// Nomi localizzati/label/descr persistiti da NTW (evita "Undefined" nel dettaglio).
						ancillaryNameIt: anyA.ancillaryNameIt ?? null,
						ancillaryNameEn: anyA.ancillaryNameEn ?? null,
						ancillaryLabel: a.ancillaryDefaultLabel ?? anyA.ancillaryLabel ?? null,
						ancillaryDescr: anyA.ancillaryDescr ?? anyA.ancillaryDescription ?? null,
						ancillaryCurrency: a.ancillaryCurrency,
						idPrcAncillary: a.idPrcAncillary,
						units: unitsOf(a.ancillaryCode),
						maxUnits: a.maxUnits ?? null,
						mileage: isMileage(a) ? mileageOf(a.ancillaryCode) : null,
						address: needsAddress(a) ? (addressOf(a.ancillaryCode) || null) : null,
						// LPP-23910: tipo e flag chilometraggio dal catalogo, allineati all'emissione.
						ancillaryType: (anyA.ancillaryType as string) ?? null,
						mileageNeeded: Boolean(anyA.mileageNeeded),
						amountTaxIncluded: a.amountTaxIncluded,
						discountPercentage: discount === null ? null : Math.round(discount * 100),
						// LPP-25233 (C2): i guidatori registrati, uno per unita'.
						additionalDriverCbIds: isAdditionalDriverAncillary(a) ? driverSlots.map((s) => s.cbId as string) : null,
						deskRules: resolveDeskRules(a)
					};
				});
			const request: IAddAncillariesBatchRequest = {
				idRentalAgreement,
				items,
				customerDigitSign: signatures.customerDigitSign,
				lastRequestUpdateDate,
				idsProfile
			};
			addMutation.mutate(request);
		} else {
			const request: IRemoveAncillariesBatchRequest = {
				idRentalAgreement,
				items: removeCodes.map((code) => {
					const ancillary = byCode.get(code) as IRADAncillary;
					return {
						ancillaryCode: code,
						// R2 (LPP-21950): unita' da rimuovere. Restano null quando l'accessorio ha una
						// sola unita', cosi' il body non cambia rispetto al comportamento storico.
						units: isMultiUnitRemovable(ancillary) ? removeUnitsOf(ancillary) : null,
						deskRules: resolveDeskRules(ancillary)
					};
				}),
				customerDigitSign: signatures.customerDigitSign,
				lastRequestUpdateDate
			};
			removeMutation.mutate(request);
		}
	};

	if (!isOngoing || (!canAdd && !canRemove)) {
		return null;
	}

	/** Placeholder ordinato quando non c'e' alcun accessorio da mostrare (catalogo vuoto o nessun rimovibile). */
	const renderEmpty = (messageKey: string) => (
		<Box data-testid={'ancillary-empty'}
			 sx={{
				 display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
				 gap: theme.spacing(1), py: theme.spacing(5), px: theme.spacing(2),
				 border: `1px dashed ${theme.palette.neutral.dark}`, borderRadius: 1, color: 'text.secondary'
			 }}>
			<Inventory2Outlined fontSize={'large'}/>
			<Typography variant={'body'} color={'text.secondary'} sx={{textAlign: 'center'}}>
				{t(messageKey)}
			</Typography>
		</Box>
	);

	/**
	 * LPP-21950 (SCFE-2) — lista di aggiunta con i Change Group accorpati in UNA voce a scelta
	 * multipla, come in emissione e come nelle sezioni "prenotati"/"aggiunti" del dettaglio RA.
	 * Prima erano N box separati e l'operatore vedeva la stessa famiglia sparsa su piu' righe.
	 *
	 * L'accorpamento e' di sola presentazione: selezionare o deselezionare nel controllo
	 * valorizza le STESSE ancillary sottostanti (units 1/0), quindi a parita' di scelta il
	 * payload inviato, il prezzo e il totale restano identici.
	 */
	const renderAddList = () => {
		const {changeGroup, others} = partitionChangeGroup<IRADAncillary>(addableAncillaries);
		return (
			<>
				{changeGroup.length > 0 && renderChangeGroupField(changeGroup.map((x) => x.ancillary))}
				{others.map((x) => renderAddRow(x.ancillary))}
			</>
		);
	};

	/** Le ancillary Change Group vendibili al banco, come singolo controllo a scelta multipla. */
	// LPP-24866: prezzo con sconto banco nel modal ADD post-firma — lordo (barrato se scontato) + netto.
	// Il netto è calcolato client-side dallo sconto selezionato (addDiscount[code], punti % interi),
	// coerente con handleConfirm (che invia lo stesso sconto). Senza sconto: solo il lordo.
	const renderPriceWithDiscount = (a: IRADAncillary, suffix: string) => {
		const pct = addDiscount[a.ancillaryCode];
		const gross = a.amountTaxIncluded;
		const currency = a.ancillaryCurrency as string;
		const discounted = typeof pct === 'number' && pct > 0;
		const net = discounted ? Math.round(gross * (100 - (pct as number)) / 100) : gross;
		return (
			<Typography variant={'body'} color={'text.secondary'}>
				{discounted &&
					<span style={{textDecoration: 'line-through'}}>{formatCurrency(locale, gross, currency)} </span>}
				{formatCurrency(locale, net, currency)}{suffix}
			</Typography>
		);
	};

	const renderChangeGroupField = (gruppo: IRADAncillary[]) => {
		const options: IChangeGroupOption<IRADAncillary>[] = gruppo.map((a) => ({
			code: a.ancillaryCode,
			label: label(a),
			ancillary: a
		}));
		const selectedCodes = gruppo
			.filter((a) => unitsOf(a.ancillaryCode) > 0)
			.map((a) => a.ancillaryCode);

		return (
			<Box sx={{py: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.neutral.light}`}}
				 data-testid={'ancillary-add-change-group'}>
				<ChangeGroupMultiSelect<IRADAncillary>
					options={options}
					selectedCodes={selectedCodes}
					testId={'post-sign-add'}
					onChange={(codes) => {
						const scelti = new Set(codes);
						// Stessa operazione dei box a checkbox: units 1 se selezionato, 0 altrimenti.
						gruppo.forEach((a) => setUnits(a, scelti.has(a.ancillaryCode) ? 1 : 0));
					}}
					renderPrice={(a) => formatCurrency(locale, a.amountTaxIncluded, a.ancillaryCurrency as string)}
				/>
				{
					// Sconto banco per ogni Change Group selezionato e scontabile (stesso gap dell'emissione):
					// il payload di handleConfirm legge già addDiscount[code], qui manca solo il controllo UI.
					// LPP-24866: per ogni Change Group selezionato mostra nome + prezzo (lordo barrato + netto se
					// scontato); la tendina sconto solo se scontabile. Prima il prezzo del CG spariva alla selezione
					// (era solo dentro l'opzione della tendina).
					gruppo
						.filter((a) => unitsOf(a.ancillaryCode) > 0)
						.map((a) => (
							<Box key={`cg-selected-${a.ancillaryCode}`}
								 sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(1), pt: theme.spacing(1)}}>
								<Box sx={{display: 'flex', flexDirection: 'column'}} data-testid={`cg-add-price-${a.ancillaryCode}`}>
									<Typography variant={'body'}>{label(a)}</Typography>
									{renderPriceWithDiscount(a, '')}
								</Box>
								{a.flagDiscountApplicable &&
									<AncillaryDiscountSelect testId={a.ancillaryCode} value={addDiscount[a.ancillaryCode] ?? null}
										flagDiscountApplicable={a.flagDiscountApplicable}
										range={deskDiscountRange}
										onChange={(percentage) => setAddDiscount((prev) => ({...prev, [a.ancillaryCode]: percentage}))}/>}
							</Box>
						))
				}
			</Box>
		);
	};

	/** Riga accessore in aggiunta: checkbox (maxUnits<=1) o stepper +/− (maxUnits>1), prezzo/unita' e sconto banco. */
	const renderAddRow = (a: IRADAncillary) => {
		const units = unitsOf(a.ancillaryCode);
		const selected = units > 0;
		const mileage = isMileage(a);
		const multi = !mileage && isMultiUnit(a);

		// LPP-22290 / Requirement 5.1: mutua esclusione coperture SC/SCDW/STP.
		// LPP-21950: esplode i Pack per verificare le coperture contenute nei figli (packAncillaries).
		// Si derivano i subtype selezionati (units > 0) e quelli già presenti sull'RA (booked + counterSale),
		// e si disabilita la riga se un'altra copertura è già attiva.
		const selectedSubtypes = [
			...addableAncillaries
				.filter((x) => unitsOf(x.ancillaryCode) > 0)
				.flatMap((x) => getEffectiveSubtypes(x)),
			...(existingAncillaries ?? [])
				.filter((x) => (x.ancillaryUnits ?? 0) > 0 || (x.ancillaryMileage ?? 0) > 0 || x.ancillarySelected)
				.flatMap((x) => getEffectiveSubtypes(x)),
		];
		const candidateSubtypes = getEffectiveSubtypes(a);
		// LPP-25233: come in emissione, telepedaggio non vendibile senza apparato sul veicolo e guidatore
		// aggiuntivo non vendibile con gli slot del RA gia' occupati.
		const blockedReason = isEtollBlocked(a, vehicleEtollNumber)
			? 'raDetail.ancillaries.postSign.message.etollNotAvailable'
			: isAdditionalDriverAncillary(a) && maxUnitsOf(a) === 0
				? 'raDetail.ancillaries.postSign.message.driverSlotsFull'
				: null;
		const coverageDisabled = candidateSubtypes.some((st: string) =>
			isCoverageDisabledByMutualExclusion(st, selectedSubtypes)
		);
		const rowDisabled = coverageDisabled || !!blockedReason;
		return (
			<Box key={a.ancillaryCode} data-testid={'ancillary-add-row'}
				 sx={{py: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.neutral.light}`}}>
				<Grid container spacing={2} alignItems={'center'}>
					<Grid item xs={12} sm={7} sx={{display: 'flex', alignItems: 'center', gap: theme.spacing(1.5)}}>
						{
							// mileageNeeded -> input km; maxUnits>1 -> NumberInput con +/−; altrimenti checkbox.
							mileage
								? <TextField type={'number'} size={'small'} data-testid={`ancillary-mileage-${a.ancillaryCode}`}
											 sx={{width: '110px'}} label={t('raFilling.ancillariesInfo.form.mileage')}
											 value={mileageOf(a.ancillaryCode)}
											 inputProps={{min: 0, max: 999}}
											 disabled={rowDisabled}
											 onChange={(e) => setMileage(a, parseInt(e.target.value || '0', 10))}/>
								: multi
									? <Box data-testid={`ancillary-units-${a.ancillaryCode}`} sx={{width: '120px'}}>
										<NumberInput
											label={''}
											defaultValue={units}
											minValue={0}
											maxValue={maxUnitsOf(a)}
											disabled={rowDisabled}
											onChange={(value: number) => setUnits(a, value)}
										/>
									</Box>
									: <Checkbox color={'accent'} data-testid={`ancillary-check-${a.ancillaryCode}`}
												checked={selected}
												disabled={rowDisabled}
												onChange={(e) => toggleAdd(a, e.target.checked)}/>
						}
						<Typography variant={'body'}>{label(a)}</Typography>
					</Grid>
					<Grid item xs={12} sm={5} sx={{display: 'flex', justifyContent: 'flex-end'}}>
						{renderPriceWithDiscount(a, mileage ? '/km' : multi ? '/u' : '')}
					</Grid>
					{
						!!blockedReason &&
						<Grid item xs={12} sx={{pt: '0 !important'}}>
							<Typography variant={'caption'} color={'text.secondary'} data-testid={`ancillary-blocked-${a.ancillaryCode}`}>
								{t(blockedReason)}
							</Typography>
						</Grid>
					}
					{
						// Indirizzo di consegna/ritiro per DELIVERY/COLLECTION/URBAN_* (come in emissione, feedback 4).
						// LPP-25233: obbligatorio a accessorio selezionato, come la regola yup dell'emissione.
						selected && needsAddress(a) &&
						<Grid item xs={12}>
							<TextField fullWidth size={'small'} required data-testid={`ancillary-address-${a.ancillaryCode}`}
								label={label(a)} value={addressOf(a.ancillaryCode)}
								error={isAddressMissing(a, units, mileageOf(a.ancillaryCode), addressOf(a.ancillaryCode))}
								helperText={isAddressMissing(a, units, mileageOf(a.ancillaryCode), addressOf(a.ancillaryCode)) ? t('common.error.required') : undefined}
								onChange={(e) => setAddressValue(a, e.target.value)}/>
						</Grid>
					}
					{
						selected && a.flagDiscountApplicable &&
						<Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
							<AncillaryDiscountSelect testId={a.ancillaryCode} value={addDiscount[a.ancillaryCode] ?? null}
								flagDiscountApplicable={a.flagDiscountApplicable}
								range={deskDiscountRange}
								onChange={(percentage) => setAddDiscount((prev) => ({...prev, [a.ancillaryCode]: percentage}))}/>
						</Grid>
					}
					{
						// LPP-25233: dati, controlli e OTP di ogni guidatore aggiuntivo, come in emissione.
						selected && isAdditionalDriverAncillary(a) &&
						driverSlots.map((slot, i) => (
							<Grid item xs={12} key={`driver-slot-${slot.key}`}>
								<PostSignAdditionalDriver
									idRentalAgreement={idRentalAgreement}
									title={`${t('otpValidation.form.title.additionalDriver')} ${existingAdditionalDrivers + i + 1}`}
									onReadyChange={(cbId) => setDriverSlotCbId(slot.key, cbId)}
									onRemove={() => removeDriverSlot(a, slot.key)}/>
							</Grid>
						))
					}
				</Grid>
			</Box>
		);
	};

	/**
	 * Riga accessore in rimozione: checkbox + nome + (R2/LPP-21950) stepper delle unita' da rimuovere
	 * quando l'accessorio ne ha piu' di una + prezzo.
	 *
	 * Lo stepper parte dalle unita' totali (rimozione completa, comportamento storico) e permette di
	 * scendere fino a 1 per una rimozione parziale. E' abilitato solo a riga selezionata, cosi' la
	 * quantita' si imposta sull'accessorio che si sta effettivamente rimuovendo.
	 */
	const renderRemoveRow = (a: IRADAncillary) => {
		const selected = removeCodes.includes(a.ancillaryCode);
		const multi = isMultiUnitRemovable(a);
		return (
			<Box key={a.ancillaryCode} data-testid={'ancillary-remove-row'}
				 sx={{py: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.neutral.light}`}}>
				<Grid container spacing={2} alignItems={'center'}>
					<Grid item xs={12} sm={7} sx={{display: 'flex', alignItems: 'center', gap: theme.spacing(1.5)}}>
						<FormControlLabel
							control={<Checkbox color={'accent'} data-testid={`ancillary-remove-check-${a.ancillaryCode}`}
											   checked={selected}
											   onChange={(e) => toggleRemove(a.ancillaryCode, e.target.checked)}/>}
							label={label(a)}/>
						{
							multi &&
							<Box data-testid={`ancillary-remove-units-${a.ancillaryCode}`} sx={{width: '120px'}}>
								<NumberInput
									label={t('raDetail.ancillaries.postSign.field.unitsToRemove')}
									defaultValue={removeUnitsOf(a)}
									minValue={1}
									maxValue={removableUnitsOf(a)}
									disabled={!selected}
									onChange={(value: number) => setRemoveUnitsValue(a, value)}
								/>
							</Box>
						}
					</Grid>
					<Grid item xs={12} sm={5} sx={{display: 'flex', justifyContent: 'flex-end'}}>
						<Typography variant={'body'} color={'text.secondary'}>
							{formatCurrency(locale, a.amountTaxIncluded, a.ancillaryCurrency as string)}
						</Typography>
					</Grid>
				</Grid>
			</Box>
		);
	};

	return (
		<Box data-testid={'ancillary-post-sign-actions'} sx={{display: 'flex', gap: theme.spacing(1), mb: theme.spacing(2)}}>
			{
				canAdd &&
				<Button variant={'contained'} color={'accent'} data-testid={'ancillary-add-button'}
						onClick={() => handleOpen('add')}>
					{t('raDetail.ancillaries.postSign.button.add')}
				</Button>
			}
			{
				canRemove &&
				<Button variant={'outlined'} color={'accent'} data-testid={'ancillary-remove-button'}
						onClick={() => handleOpen('remove')}>
					{t('raDetail.ancillaries.postSign.button.remove')}
				</Button>
			}

			<Dialog open={mode !== null} onClose={handleClose} maxWidth={'lg'} fullWidth
					data-testid={'ancillary-post-sign-dialog'}>
				<DialogTitle>
					{
						mode === 'add'
							? t('raDetail.ancillaries.postSign.dialog.addTitle')
							: t('raDetail.ancillaries.postSign.dialog.removeTitle')
					}
				</DialogTitle>

				<DialogContent dividers sx={{p: 3}}>
					<Stack spacing={3}>
						{
							mode === 'add' &&
							<Box>
								{
									addableAncillaries.length === 0
										? renderEmpty('raDetail.ancillaries.postSign.emptyAddList')
										: renderAddList()
								}
							</Box>
						}

						{
							mode === 'remove' &&
							<Box>
								{
									removableAncillaries.length === 0
										? renderEmpty('raDetail.ancillaries.postSign.emptyRemoveList')
										: removableAncillaries.map(renderRemoveRow)
								}
							</Box>
						}

						<Divider/>

						{/* Firma unica per l'intero blocco. Il canvas e' vincolato al 100% del dialog. */}
						<Box sx={{'& canvas': {maxWidth: '100%', height: 'auto'}, '& .MuiBox-root': {maxWidth: '100%'}}}>
							<AncillarySignatureBox
								onSignatureChange={(sig: IDigitSignPair, customerPresent: boolean) => {
									setSignatures(sig);
									setSignCustomerPresent(customerPresent);
								}}/>
						</Box>
					</Stack>

					<CustomLoader loading={isLoading}/>
				</DialogContent>

				<DialogActions>
					<Button variant={'outlined'} color={'accent'} onClick={handleClose}>
						{t('digitalSign.label.cancel')}
					</Button>
					<Button variant={'contained'} color={'accent'} data-testid={'ancillary-confirm-button'}
							disabled={!canConfirm} onClick={handleConfirm}>
						{t('digitalSign.label.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default AncillaryPostSignActions;
